import { Badge } from '@/components/ui/Badge';
import type { BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { reportItems } from '@/hooks/useInventoryImport';
import type {
  ImportFieldDiff,
  ImportReportCategory,
  ImportReportItem,
  InventoryImportReport,
} from '@/hooks/useInventoryImport';

/**
 * Diff-Ansicht des Inventar-Import-Reports (Spec S8-06 §5).
 *
 * Rein präsentational: Auswahl-State lebt in der Route. Constraints aus der
 * Spec: `unmatched_tool_only` hat KEINE Checkboxen (reine Info, der Import
 * löscht nie); `conflicts` ist selektierbar, aber nie vorausgewählt.
 */

interface SectionConfig {
  category: ImportReportCategory;
  title: string;
  description: string;
  tone: BadgeTone;
  /** false -> keine Checkboxen, keine Alle/Keine-Toggles. */
  selectable: boolean;
}

const DIFF_COLUMNS: ReadonlyArray<Column<ImportFieldDiff>> = [
  {
    id: 'feld',
    header: 'Feld',
    className: 'font-mono text-xs',
    cell: (diff) => diff.field,
  },
  {
    id: 'alt',
    header: 'Alt',
    className: 'font-mono text-xs text-muted',
    cell: (diff) => formatDiffValue(diff.old),
  },
  {
    id: 'neu',
    header: 'Neu',
    className: 'font-mono text-xs',
    cell: (diff) => formatDiffValue(diff.new),
  },
];

const SECTIONS: SectionConfig[] = [
  {
    category: 'new_customers',
    title: 'Neue Kunden',
    description: 'Im Inventar gefunden, im Tool noch nicht vorhanden.',
    tone: 'success',
    selectable: true,
  },
  {
    category: 'new_locations',
    title: 'Neue Standorte',
    description: 'Standorte, die beim jeweiligen Kunden angelegt werden.',
    tone: 'success',
    selectable: true,
  },
  {
    category: 'new_systems',
    title: 'Neue Systeme',
    description: 'Hosts ohne Treffer im Tool — werden als neue Systeme angelegt.',
    tone: 'success',
    selectable: true,
  },
  {
    category: 'changed',
    title: 'Änderungen',
    description: 'Gematchte Systeme mit Feld-Unterschieden (alt -> neu).',
    tone: 'primary',
    selectable: true,
  },
  {
    category: 'conflicts',
    title: 'Konflikte',
    description:
      'Nicht eindeutig auflösbar (z. B. Alias kollidiert). Nie vorausgewählt — nur nach manueller Prüfung übernehmen.',
    tone: 'danger',
    selectable: true,
  },
  {
    category: 'unmatched_tool_only',
    title: 'Nur im Tool',
    description:
      'Systeme im Tool ohne Gegenstück im Inventar. Reine Info — der Import löscht nie.',
    tone: 'neutral',
    selectable: false,
  },
];

interface ImportReportViewProps {
  report: InventoryImportReport;
  selection: ReadonlySet<string>;
  onToggleItem: (itemId: string, checked: boolean) => void;
  /** Alle/Keine-Toggle einer Sektion: setzt alle übergebenen IDs auf checked. */
  onSetSection: (itemIds: string[], checked: boolean) => void;
}

export function ImportReportView({
  report,
  selection,
  onToggleItem,
  onSetSection,
}: ImportReportViewProps): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      {SECTIONS.map((section) => (
        <ReportSection
          key={section.category}
          config={section}
          items={reportItems(report, section.category)}
          selection={selection}
          onToggleItem={onToggleItem}
          onSetSection={onSetSection}
        />
      ))}
    </div>
  );
}

function ReportSection({
  config,
  items,
  selection,
  onToggleItem,
  onSetSection,
}: {
  config: SectionConfig;
  items: ImportReportItem[];
  selection: ReadonlySet<string>;
  onToggleItem: (itemId: string, checked: boolean) => void;
  onSetSection: (itemIds: string[], checked: boolean) => void;
}): JSX.Element {
  const itemIds = items.map((item) => item.item_id);
  const selectedCount = itemIds.filter((id) => selection.has(id)).length;
  const showToggles = config.selectable && items.length > 0;

  return (
    <Card
      title={
        <span className="flex flex-wrap items-center gap-2">
          {config.title}
          <Badge tone={config.tone}>{items.length}</Badge>
          {showToggles && (
            <span className="text-xs font-normal text-muted">
              {selectedCount} von {items.length} ausgewählt
            </span>
          )}
        </span>
      }
      description={config.description}
      actions={
        showToggles ? (
          <>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onSetSection(itemIds, true)}
              disabled={selectedCount === items.length}
            >
              Alle
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onSetSection(itemIds, false)}
              disabled={selectedCount === 0}
            >
              Keine
            </Button>
          </>
        ) : undefined
      }
    >
      {items.length === 0 ? (
        <p className="text-sm text-muted">Keine Einträge.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((item) => (
            <ReportItemRow
              key={item.item_id}
              item={item}
              selectable={config.selectable}
              checked={selection.has(item.item_id)}
              onToggle={onToggleItem}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ReportItemRow({
  item,
  selectable,
  checked,
  onToggle,
}: {
  item: ImportReportItem;
  selectable: boolean;
  checked: boolean;
  onToggle: (itemId: string, checked: boolean) => void;
}): JSX.Element {
  const checkboxId = `import-item-${item.item_id}`;
  const details = formatDetails(item.details);
  const heading = (
    <span className="flex flex-wrap items-center gap-2">
      <span>{item.title}</span>
      <Badge tone="neutral" className="font-mono">
        {item.kind}
      </Badge>
    </span>
  );

  return (
    <li className="rounded-md border border-border bg-surface p-3">
      <div className="flex items-start gap-3">
        {selectable && (
          <input
            id={checkboxId}
            type="checkbox"
            checked={checked}
            onChange={(event) => onToggle(item.item_id, event.target.checked)}
            className="mt-1"
            aria-label={`${item.title} uebernehmen`}
          />
        )}
        <div className="min-w-0 flex-1">
          {selectable ? (
            <label
              htmlFor={checkboxId}
              className="cursor-pointer text-sm font-medium text-text"
            >
              {heading}
            </label>
          ) : (
            <p className="text-sm font-medium text-text">{heading}</p>
          )}
          {details && <p className="mt-1 text-xs text-muted">{details}</p>}
          {item.diff && item.diff.length > 0 && (
            <div className="mt-2">
              <DataTable
                caption={`Feld-Änderungen für ${item.title}`}
                columns={DIFF_COLUMNS}
                rows={item.diff}
                rowKey={(diff) => diff.field}
                filters={false}
              />
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function formatDiffValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '–';
  if (typeof value === 'boolean') return value ? 'ja' : 'nein';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    // JSON.stringify scheitert nur bei Zyklen; String(obj) wäre '[object Object]'.
    return '[nicht darstellbar]';
  }
}

function formatDetails(details: unknown): string | null {
  if (details === null || details === undefined || details === '') return null;
  if (typeof details === 'string') return details;
  try {
    return JSON.stringify(details);
  } catch {
    return '[nicht darstellbar]';
  }
}
